import cx from "classnames";
import { useFormikContext } from "formik";
import { DatePicker } from "../";
import { Select } from "../../ui";
import SelectCounter from "../../ui/SelectCounter/SelectCounter";
import { client } from "../../utils/network";
import { useState, useEffect } from "react";
import "./Sort.scss";
import { DOMAIN_API_HOST } from './../../configs/main';
import { useSnackbar } from "notistack";
import useCampaigns from "../../data/useCampaigns";
import {
  INTERESTS_HIDDEN_USER_ID,
  getAllCampaignsSelection,
} from "../../modules/Statistics/specialClientMockStats";

const getDefaultCounterAndCampaign = (counters, campaignsList, preferredCampaignId) => {
  if (!counters?.length) return { counterId: null, campaignId: null };

  const list = campaignsList || [];

  if (preferredCampaignId) {
    const counter = counters.find((c) =>
      (c.basic_configs || []).some((b) => b.campaign_id === preferredCampaignId)
    );
    if (counter) {
      return { counterId: counter.id, campaignId: preferredCampaignId };
    }
  }

  const activeCampaign = list.find((c) => c?.status === "ACTIVE");
  if (activeCampaign?.id) {
    const counter = counters.find((c) =>
      (c.basic_configs || []).some((b) => b.campaign_id === activeCampaign.id)
    );
    if (counter) {
      return { counterId: counter.id, campaignId: activeCampaign.id };
    }
  }

  for (const counter of counters) {
    const config = (counter.basic_configs || []).find((b) => {
      const camp = list.find((x) => x.id === b.campaign_id);
      return camp?.status === "ACTIVE";
    });
    if (config) {
      return { counterId: counter.id, campaignId: config.campaign_id };
    }
  }

  const firstConfig = counters[0]?.basic_configs?.[0];
  return {
    counterId: counters[0].id,
    campaignId: firstConfig?.campaign_id || null,
  };
};

export function StatisticsSort({ userData, onRefresh }) {
  const formik = useFormikContext();
  const [counters, setCounters] = useState([]);
  const [prevSelectedGoals, setPrevSelectedGoals] = useState([]);
  const [prevSelectedCampaigns, setPrevSelectedCampaigns] = useState([]);
  const [prevSelectedAds, setPrevSelectedAds] = useState([]);
  const [ads, setAds] = useState([]);
  const { campaigns } = useCampaigns();
  const { enqueueSnackbar } = useSnackbar();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const SPECIAL_USER_ID = "0aeb7a8a-7120-49ab-b62c-13158dc69304";
  const SPECIAL_CAMPAIGN_ID = "4b0406ab-127c-4563-bd66-17bb0b9c343e";
  const hideAdsFilter =
    Boolean(userData.user?.cabinet_visibility?.hide_ads_filter) ||
    ["84149b79-02d9-4919-8a92-e2531594dc4b", "d7de6dcb-228b-479b-9226-855853edc9e5", "eab9761d-5e15-4212-b09b-50bbc86ed90c"].includes(userData.user.id);
  const hideGoalsFilter =
    Boolean(userData.user?.cabinet_visibility?.hide_goals_filter) ||
    userData.user.is_trial ||
    userData.user.id === "6d485413-ee24-4963-9f74-51feb24f761c";
  const hidePostviewDownload =
    Boolean(userData.user?.cabinet_visibility?.hide_postview_download) ||
    ["eab9761d-5e15-4212-b09b-50bbc86ed90c", "c6ecfcdf-9fd6-4a31-8aea-1bc112a8b15e"].includes(userData.user.id);
  const hidePostviewButton =
    ["d7de6dcb-228b-479b-9226-855853edc9e5", "6d485413-ee24-4963-9f74-51feb24f761c"].includes(userData.user.id);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(async () => {
    try {
      const { data } = await client("/api/v1/ya-metrika/counters");
      const campaigns = await client("/api/v1/campaigns?upd=true");
      if (!userData.user.is_trial && (!data || !data.length)) {
        return setCounters([
          {
            optionValue: "",
            optionLabel: "У Вас нет созданных счетчиков",
            id: 1,
          },
        ]);
      }

      let processedCounters;
      if (userData.user.id === "84149b79-02d9-4919-8a92-e2531594dc4b") {
        processedCounters = data.filter((counter) => counter.id == "ff210e98-1d87-42c2-831c-6f0b9e1f1010").map((counter) => ({
          optionValue: counter.id,
          optionLabel: counter.name,
          ...counter,
        }));
      } else {
        processedCounters = data.map((counter) => ({
          optionValue: counter.id,
          optionLabel: counter.name,
          ...counter,
        }));
      }

      setCounters(processedCounters);

      if (userData.user.is_trial) {
        if (campaigns && campaigns.data.length > 0) {
          formik.setFieldValue("metric_counter", "9b1b3890-ae9f-40ff-999f-3f0ded7838fb");
          const activeCampaign = campaigns.data.find(
            (campaign) => campaign?.status === "ACTIVE"
          ) || campaigns.data[0];
          if (activeCampaign?.id) {
            formik.setFieldValue("selected_campaigns", [activeCampaign.id]);
          }
        }
      } else {
        if (!formik.values.metric_counter && processedCounters.length > 0) {
          const campaignsList = campaigns?.data || [];
          const preferredCampaignId =
            userData.user.id === SPECIAL_USER_ID ? SPECIAL_CAMPAIGN_ID : null;
          const { counterId, campaignId } = getDefaultCounterAndCampaign(
            processedCounters,
            campaignsList,
            preferredCampaignId
          );
          if (counterId) {
            formik.setFieldValue("metric_counter", counterId);
          }
          if (campaignId || userData.user.id === INTERESTS_HIDDEN_USER_ID) {
            if (userData.user.id === INTERESTS_HIDDEN_USER_ID) {
              const counter = processedCounters.find((item) => item.id === counterId);
              formik.setFieldValue("selected_campaigns", getAllCampaignsSelection(counter, campaignsList));
            } else {
              formik.setFieldValue("selected_campaigns", [campaignId]);
            }
          }
        }
      }

    } catch (err) {
      console.log(err);
    }
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      const myHeaders = new Headers();
      myHeaders.append("Content-Type", "application/json");
      myHeaders.append(
        "Authorization",
        "Bearer " + localStorage.getItem("token")
      )

      formik.values?.selected_campaigns?.filter(x => x != "all_campaigns").forEach(async (campaignId) => {
        const response = await fetch(`${DOMAIN_API_HOST}/api/v1/advertisements/fetch?campaign_id=${campaignId}`, {
          method: "GET",
          headers: myHeaders,
        });
        if (response.ok) {
          const data = await response.json();

          const sortedAds = data.sort(
            (a, b) => new Date(b.created_at) - new Date(a.created_at)
          );

          setAds((ads) => [...ads, ...sortedAds]);
        }
      })
    }
    if (!formik.values?.metric_counter && !userData.user.is_trial) return;
    setAds([])
    fetchData()
  }, [formik.values.selected_campaigns])



  let campaignsOptions = [];
  const createAllCampaignsOption = () => ({
    label: "Все кампании",
    value: "all_campaigns",
    selected: formik.values?.selected_campaigns?.includes("all_campaigns"),
  });
  if (userData.user.is_trial) {
    let arr = []
    if (campaigns?.length === 0) {
      arr.push({
        label: "Нет кампаний",
        value: "0",
        selected: false
      })
    } else {
      campaigns?.map((campaign) => {
        if (campaign.status !== "DELETED") {
          arr.push({
            label: campaign.basic_config.name,
            value: campaign.id,
            selected: formik.values?.selected_campaigns?.some(
              (item) => item === campaign.id)
          })
        }
      })
    }
    if (!arr.some((c) => c.label === "Нет кампаний")) {
      campaignsOptions = [createAllCampaignsOption(), ...arr];
    } else {
      campaignsOptions = arr;
    }
  } else {
    let arr = []
    if (formik.values?.metric_counter) {
      counters.map((item) => {
        if (item.id === formik.values?.metric_counter) {
          const activeBasicConfigs = (item?.basic_configs || []).filter((b) => {
            const c = campaigns?.find?.((x) => x.id === b.campaign_id);
            return c && c.status !== "DELETED";
          });
          (activeBasicConfigs.length ? activeBasicConfigs : [{
            name: "Нет кампаний",
            campaign_id: "0"
          }]).map((campaign) =>
            arr.push({
              label: campaign.name,
              value: campaign.campaign_id,
              selected: formik.values?.selected_campaigns?.some(
                (item) => item === campaign.campaign_id
              )
            })
          );
        }
      });
      if (!arr.some((c) => c.label === "Нет кампаний")) {
        campaignsOptions = [createAllCampaignsOption(), ...arr];
      } else {
        campaignsOptions = arr;
      }
    }
  }


  let adsOptions = [];
  const createAllAdsOption = () => ({
    label: "Все объявления",
    value: "all_ads",
    selected: formik.values?.selected_ads?.includes("all_ads"),
  });

  if (userData.user.is_trial) {
    let arr = []
    if (ads.length === 0) {
      arr.push({
        label: "Нет объявлений",
        value: "0",
        selected: false
      })
    } else {
      ads.map((ad) => {
        arr.push({
          label: ad.head,
          ad: ad,
          value: ad.id,
          selected: formik.values?.selected_ads?.some(
            (item) => ad.id === item)
        })
      });
    }
    if (!arr.some((a) => a.label === "Нет объявлений")) {
      adsOptions = [createAllAdsOption(), ...arr];
    } else {
      adsOptions = arr;
    }
  } else {
    if ((formik.values?.metric_counter) && formik.values?.selected_campaigns) {
      let arr = []
      if (ads.length === 0) {
        arr.push({
          label: "Нет объявлений",
          value: "0",
          selected: false
        })
      } else {
        ads.map((ad) => {
          arr.push({
            label: ad.head,
            ad: ad,
            value: ad.id,
            selected: formik.values?.selected_ads?.some(
              (item) => ad.id === item)
          })
        });
      }
      if (!arr.some((a) => a.label === "Нет объявлений")) {
        adsOptions = [createAllAdsOption(), ...arr];
      } else {
        adsOptions = arr;
      }
    }
  }

  const createAllGoalsOption = () => ({
    label: "Все цели",
    value: "all_goals",
    selected: formik.values?.selected_goals?.includes("all_goals"),
  });

  let goalOptions = []
  if (formik.values?.metric_counter) {
    let arr = []
    counters.map((item) => {
      if (item.id === formik.values?.metric_counter) {
        if (item?.goals?.length !== 0) {
          item?.goals?.map((goal) =>
            arr.push({
              label: goal.name,
              value: goal.id,
              selected: formik.values?.selected_goals?.some(
                (item) => item === goal.id
              ),
            }))
        } else arr.push({
          label: "Нет целей",
          value: "0",
          selected: null
        })
      }
    });
    if (!arr.some((goal) => goal.label === "Нет целей")) {
      goalOptions = [createAllGoalsOption(), ...arr];
    } else {
      goalOptions = arr
    }
  }

  const XOROptions = {
    values: [],
    init(initialValues) {
      this.values = initialValues;
    },
    push(value) {
      if (this.values.includes(value)) {
        this.value = this.values.splice(this.values.indexOf(value), 1);
      } else {
        this.values.push(value)
      }
    }
  }

  return (
    <div className="Sort">
      <div style={{ marginBottom: isMobile ? 0 : 20 }} className={cx("container", "container-statistics")}>
        {!userData.user.is_trial && (
          <SelectCounter
            disabled={userData?.user?.is_trial}
            options={counters}
            value={formik.values.metric_counter ?? ""}
            name="metric_counter"
            placeholder="Счетчики"
            onChange={selected => {
              formik.setFieldValue("metric_counter", selected)
              if (userData.user.id === SPECIAL_USER_ID) {
                formik.setFieldValue("selected_campaigns", [SPECIAL_CAMPAIGN_ID])
              } else if (userData.user.id === INTERESTS_HIDDEN_USER_ID) {
                const counter = counters.find((item) => item.id === selected);
                formik.setFieldValue("selected_campaigns", getAllCampaignsSelection(counter, campaigns));
              } else {
                formik.setFieldValue("selected_campaigns", [])
              }
              formik.setFieldValue("selected_goals", [])
            }}
          />
        )}
        {userData.user.id !== "84149b79-02d9-4919-8a92-e2531594dc4b" && (
          <Select
            isCampaign
            isMobile={isMobile}
            placeholder="Кампании"
            options={userData.user.is_trial ? campaignsOptions : formik.values?.metric_counter ? campaignsOptions : []}
            onChange={(selectedOptions) => {
              let selectedValues = selectedOptions?.map(({ value }) => value) || []

              console.log("selectedValues", selectedValues)

              XOROptions.init([...prevSelectedCampaigns]);
              selectedValues.forEach(element => XOROptions.push(element));

              const changedOption = XOROptions.values[0];
              const isAddedNewOption = selectedValues.length > prevSelectedCampaigns.length;
              const isAllCampaignsSelected = () => !campaignsOptions.filter(({ value }) => !selectedValues.includes(value) && value !== "all_campaigns").length;
              const removeAllCampaignsOption = () => {
                if (selectedValues.indexOf("all_campaigns") !== -1) {
                  selectedValues.splice(selectedValues.indexOf("all_campaigns"), 1);
                }
              }
              if (isAddedNewOption && changedOption == "all_campaigns") {
                const _options = campaignsOptions.map(({ value }) => value);
                formik.setFieldValue("selected_campaigns", _options);
                setPrevSelectedCampaigns(_options);
                return true
              } else if (!isAddedNewOption && changedOption == "all_campaigns") {
                formik.setFieldValue("selected_campaigns", []);
                setPrevSelectedCampaigns([]);
                return true
              } else if (isAddedNewOption && changedOption != "all_campaigns") {
                if (isAllCampaignsSelected()) {
                  selectedValues.push("all_campaigns");
                } else {
                  removeAllCampaignsOption();
                }
              } else if (!isAddedNewOption && changedOption != "all_campaigns") {
                removeAllCampaignsOption();
              }

              formik.setFieldValue("selected_campaigns", selectedValues);

              setPrevSelectedCampaigns(selectedValues);
            }}
          />
        )}

        {!hideAdsFilter && (
          <Select
            isAd
            isMobile={isMobile}
            placeholder="Объявления"
            // options={(formik.values?.metric_counter && formik.values?.selected_campaigns) ? adsOptions : []}
            options={userData.user.is_trial ? formik.values?.selected_campaigns ? adsOptions : [] : formik.values?.metric_counter && formik.values?.selected_campaigns ? adsOptions : []}
            onChange={(selectedOptions) => {
              let selectedValues = selectedOptions?.map(({ value }) => value) || []

              XOROptions.init([...prevSelectedAds]);
              selectedValues.forEach(element => XOROptions.push(element));

              const changedOption = XOROptions.values[0];
              const isAddedNewOption = selectedValues.length > prevSelectedAds.length;
              const isAllAdsSelected = () => !adsOptions.filter(({ value }) => !selectedValues.includes(value) && value !== "all_ads").length;
              const removeAllAdsOption = () => {
                if (selectedValues.indexOf("all_ads") !== -1) {
                  selectedValues.splice(selectedValues.indexOf("all_ads"), 1);
                }
              }
              if (isAddedNewOption && changedOption == "all_ads") {
                const _options = adsOptions.map(({ value }) => value);
                formik.setFieldValue("selected_ads", _options);
                setPrevSelectedAds(_options);
                return true
              } else if (!isAddedNewOption && changedOption == "all_ads") {
                formik.setFieldValue("selected_ads", []);
                setPrevSelectedAds([]);
                return true
              } else if (isAddedNewOption && changedOption != "all_ads") {
                if (isAllAdsSelected()) {
                  selectedValues.push("all_ads");
                } else {
                  removeAllAdsOption();
                }
              } else if (!isAddedNewOption && changedOption != "all_ads") {
                removeAllAdsOption();
              }

              formik.setFieldValue("selected_ads", selectedValues);

              setPrevSelectedAds(selectedValues);
            }}
          />
        )}

        {!hideGoalsFilter && (
          <Select
            isMobile={isMobile}
            placeholder="Цели"
            options={formik.values?.metric_counter ? goalOptions : []}
            onChange={(selectedOptions) => {
              let selectedValues = selectedOptions?.map(({ value }) => value) || []

              XOROptions.init([...prevSelectedGoals]);
              selectedValues.forEach(element => XOROptions.push(element));

              const changedOption = XOROptions.values[0];
              const isAddedNewOption = selectedValues.length > prevSelectedGoals.length;
              const isAllGoalsSelected = () => !goalOptions.filter(({ value }) => !selectedValues.includes(value) && value !== "all_goals").length;
              const removeAllGoalsOption = () => {
                if (selectedValues.indexOf("all_goals") !== -1) {
                  selectedValues.splice(selectedValues.indexOf("all_goals"), 1);
                }
              }
              if (isAddedNewOption && changedOption == "all_goals") {
                const _options = goalOptions.map(({ value }) => value);
                formik.setFieldValue("selected_goals", _options);
                setPrevSelectedGoals(_options);
                return true
              } else if (!isAddedNewOption && changedOption == "all_goals") {
                formik.setFieldValue("selected_goals", []);
                setPrevSelectedGoals([]);
                return true
              } else if (isAddedNewOption && changedOption != "all_goals") {
                if (isAllGoalsSelected()) {
                  selectedValues.push("all_goals");
                } else {
                  removeAllGoalsOption();
                }
              } else if (!isAddedNewOption && changedOption != "all_goals") {
                removeAllGoalsOption();
              }

              formik.setFieldValue("selected_goals", selectedValues);

              setPrevSelectedGoals(selectedValues);
            }}
          />
        )}

        <DatePicker userData={userData} />
        <button
          style={{
            width: isMobile ? "100%" : "fit-content",
            margin: isMobile ? "10px 0 0 0" : "0",
            backgroundColor: "#3f73f9",
            color: "#fff",
            padding: "8px 15px",
            fontSize: 14,
            borderRadius: 5,
            border: "none",
            cursor: "pointer"
          }}
          onClick={() => {
            if (onRefresh) {
              onRefresh();
            }
          }}
        >
          Обновить статистику
        </button>
        {!hidePostviewDownload && (
          <div style={{ display: "flex", flexDirection: "column", gap: 10, width: isMobile ? "100%" : "fit-content" }}>
            <button
              hidden={hidePostviewButton}
              style={{ width: isMobile ? "100%" : "fit-content", margin: isMobile ? "10px 0 0 0" : "0", backgroundColor: "#3f73f9", color: "#fff", padding: "8px 15px", fontSize: 14, borderRadius: 5 }}
              onClick={async () => {
                const myHeaders = new Headers();
                myHeaders.append("Content-Type", "application/json");
                myHeaders.append(
                  "Authorization",
                  "Bearer " + localStorage.getItem("token")
                );

                const addOneDay = (date) => {
                  const newDate = new Date(date);
                  newDate.setDate(newDate.getDate() + 1);
                  return newDate;
                };

                const adjustDateEnd = (dateEnd, userId) => {
                  if (!dateEnd) return dateEnd;

                  const SPECIAL_CLIENT_ID = "a1856853-7f15-427a-ad58-3c86aca153de";

                  if (userId === SPECIAL_CLIENT_ID) {
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);

                    const endDate = new Date(dateEnd);
                    endDate.setHours(0, 0, 0, 0);

                    if (endDate.getTime() === today.getTime()) {
                      const yesterday = new Date(today);
                      yesterday.setDate(yesterday.getDate() - 1);
                      return yesterday;
                    }
                  }

                  return dateEnd;
                };

                const campaigns = formik.values.selected_campaigns.filter(campaign => campaign !== "all_campaigns")
                if (campaigns.length !== 1) {
                  return enqueueSnackbar("Выберите одну кампанию для экспорта PostView", {
                    variant: "info",
                    autoHideDuration: 3000,
                    anchorOrigin: { vertical: "top", horizontal: "right" },
                  });
                }

                const _startDate = typeof formik.values.media_plan.date_start === "number" ? new Date(formik.values.media_plan.date_start) : formik.values.media_plan.date_start
                const _endDateRaw = typeof formik.values.media_plan.date_end === "number" ? new Date(formik.values.media_plan.date_end) : formik.values.media_plan.date_end
                const _endDate = adjustDateEnd(_endDateRaw, userData.user?.id)

                const date_start = new Date(`${_startDate.getFullYear()}.${_startDate.getMonth() + 1}.${_startDate.getDate()} 00:00:00+03:00`).getTime() / 1000
                const date_end = addOneDay(new Date(`${_endDate.getFullYear()}.${_endDate.getMonth() + 1}.${_endDate.getDate()} 00:00:00+03:00`)).getTime() / 1000

                const response = await fetch(
                  `${DOMAIN_API_HOST}/api/v1/marketing/post-views/export/${campaigns[0]}?date_from=${date_start}&date_to=${date_end}&date_type=conversions` + formik.values.selected_goals.filter(goal => goal !== "all_goals").map(goal => `&goals_ids=${goal}`).join(""),
                  {
                    method: "GET",
                    headers: myHeaders,
                  }
                );

                if (response.ok) {
                  const blob = await response.blob();
                  const url = window.URL.createObjectURL(blob);
                  const link = document.createElement("a");
                  link.href = url;

                  link.download = `post_view_report.xlsx`;
                  document.body.appendChild(link);
                  link.click();

                  link.remove();
                  window.URL.revokeObjectURL(url);
                } else {
                  console.error("Ошибка при скачивании файла:", response.statusText);
                }
              }}
            >
              Скачать PostView
            </button>
          </div>
        )}
      </div>
    </div>
  );
}